// Command tokenizer + argument reader (ARCH UI-5). PURE (ENG-1): commands run in
// the sim against a CommandContext. Parse failures throw CommandError carrying a
// character position; the dispatcher turns those into a result (never throws to
// the caller — E-23).
import type { CommandContext } from './core';

export interface Token {
  text: string;
  start: number;
}

export class CommandError extends Error {
  constructor(message: string, readonly pos?: number) {
    super(message);
    this.name = 'CommandError';
  }
}

export function tokenize(s: string): Token[] {
  const out: Token[] = [];
  let i = 0;
  while (i < s.length) {
    while (i < s.length && s[i] === ' ') i++;
    if (i >= s.length) break;
    const start = i;
    while (i < s.length && s[i] !== ' ') i++;
    out.push({ text: s.slice(start, i), start });
  }
  return out;
}

/** Sequential reader over a command's argument tokens with positioned errors. */
export class ArgReader {
  private i = 0;
  constructor(private readonly toks: Token[], private readonly ctx: CommandContext) {}

  get remaining(): number {
    return this.toks.length - this.i;
  }
  hasMore(): boolean {
    return this.i < this.toks.length;
  }
  private endPos(): number {
    const last = this.toks[this.toks.length - 1];
    return last ? last.start + last.text.length : 0;
  }
  private next(what: string): Token {
    if (this.i >= this.toks.length) throw new CommandError(`Expected ${what}`, this.endPos());
    return this.toks[this.i++]!;
  }

  int(what = 'integer'): number {
    const t = this.next(what);
    const n = Number(t.text);
    if (!Number.isInteger(n)) throw new CommandError(`Expected ${what}, got '${t.text}'`, t.start);
    return n;
  }

  private coord(base: number, asBlock: boolean): number {
    const t = this.next('coordinate');
    const b = asBlock ? Math.floor(base) : base;
    if (t.text === '~') return b;
    if (t.text.startsWith('~')) {
      const off = Number(t.text.slice(1));
      if (!Number.isFinite(off)) throw new CommandError(`Invalid relative coordinate '${t.text}'`, t.start);
      return asBlock ? b + Math.trunc(off) : b + off;
    }
    const n = Number(t.text);
    if (!Number.isFinite(n)) throw new CommandError(`Expected coordinate, got '${t.text}'`, t.start);
    return asBlock ? Math.floor(n) : n;
  }
  /** Block-grid coordinate (~ is relative to the player's block; integer result). */
  blockCoord(base: number): number {
    return this.coord(base, true);
  }
  /** Continuous coordinate (for teleport). */
  posCoord(base: number): number {
    return this.coord(base, false);
  }

  word(what = 'argument'): string {
    return this.next(what).text;
  }

  block(): string {
    const t = this.next('block');
    if (!this.ctx.hasBlock(t.text)) throw new CommandError(`Unknown block '${t.text}'`, t.start);
    return t.text;
  }

  choice(options: readonly string[], what = 'option'): string {
    const t = this.next(what);
    if (!options.includes(t.text)) {
      throw new CommandError(`Expected ${options.join('|')}, got '${t.text}'`, t.start);
    }
    return t.text;
  }

  end(): void {
    if (this.i < this.toks.length) {
      throw new CommandError('Too many arguments', this.toks[this.i]!.start);
    }
  }
}
